import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, ArrowLeft, Search, Compass, AlertTriangle } from 'lucide-react';
import StandardSpinner from '../../utils/Spinner';
import { useTheme } from '../../Context/themeContext';
//import { MdDashboard } from 'react-icons/md';

const NotFound = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [loading, setloading] = useState(true);

  useEffect(() => {
    setTimeout(() => {
      setloading(false);
    }, 800);
  }, []);

  if (loading) {
    return <StandardSpinner />;
  }

  // Theme-aware classes
  const isDark = theme === 'dark';
  
  const bgPrimary = isDark ? 'bg-richblue-950' : 'bg-gray-50';
  const bgSecondary = isDark ? 'bg-richblue-800' : 'bg-gray-200';
  const bgHover = isDark ? 'hover:bg-richblue-700' : 'hover:bg-gray-300';
  const borderColor = isDark ? 'border-blue-800/40' : 'border-gray-300';
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textSecondary = isDark ? 'text-blue-200/70' : 'text-gray-600';
  const textMuted = isDark ? 'text-blue-200/50' : 'text-gray-400';
  const gradientText = isDark 
    ? 'from-blue-400 to-purple-400'
    : 'from-blue-600 to-purple-600';
  const buttonGradient = isDark
    ? 'from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
    : 'from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600';
  const orbColor = isDark
    ? 'bg-blue-600/20'
    : 'bg-blue-400/20';
  const shadowColor = isDark ? 'shadow-blue-600/30' : 'shadow-blue-500/20';

  // Quick links for common pages
  const quickLinks = [
    { label: 'Home', path: '/', icon: Home },
    { label: 'Dashboard', path: '/dashboard', icon: Compass},
    { label: 'Login', path: '/login', icon: ArrowLeft },
  ];

  return (
    <div className={`min-h-screen ${bgPrimary} flex items-center justify-center px-4 transition-colors duration-300 relative overflow-hidden`}>
      {/* Animated background orbs */}
      <div className={`absolute top-20 left-20 w-96 h-96 ${orbColor} rounded-full blur-[120px] animate-pulse-slow`}></div>
      <div className={`absolute bottom-20 right-20 w-96 h-96 ${orbColor} rounded-full blur-[120px] animate-pulse-slow animation-delay-2000`}></div>
      
      {/* Grid overlay */}

      <div className="relative z-10 w-full max-w-2xl text-center">
        {/* 404 Number with Icon */}
        <div className="relative inline-block">
          <div className={`absolute -top-8 -right-8 w-16 h-16 ${isDark ? 'bg-yellow-500/20' : 'bg-yellow-400/20'} rounded-full flex items-center justify-center animate-bounce`}>
            <AlertTriangle className={`w-6 h-6 ${isDark ? 'text-yellow-500' : 'text-yellow-600'}`} />
          </div>
          <h1 className={`text-8xl md:text-9xl font-bold bg-gradient-to-r ${gradientText} text-transparent bg-clip-text mb-4`}>
            404
          </h1>
        </div>
        
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className={`p-4 rounded-full ${isDark ? 'bg-blue-600/20' : 'bg-blue-100'} border ${borderColor}`}>
            <Compass className={`w-12 h-12 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
          </div>
        </div>
        
        {/* Message */}
        <h2 className={`text-2xl md:text-3xl font-semibold ${textPrimary} mb-2`}>
          Oops! Page Not Found
        </h2>
        <p className={`${textSecondary} mb-2 max-w-md mx-auto`}>
          The page you're looking for doesn't exist or has been moved.
        </p>
        <p className={`${textMuted} text-sm mb-8`}>
          Error Code: 404 - Resource Not Found
        </p>
        
        {/* Search Suggestion */}
        <div className={`mb-8 p-4 ${isDark ? 'bg-white/5' : 'bg-gray-100'} rounded-xl border ${borderColor}`}>
          <p className={`text-sm ${textMuted} flex items-center justify-center gap-2`}>
            <Search className="w-4 h-4" />
            Try going back to the previous page or visit our homepage
          </p>
        </div>
        
        {/* Quick Links */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {quickLinks.map((link) => {
            const Icon = link.icon;
            return (
              <button
                key={link.path}
                onClick={() => navigate(link.path)}
                className={`flex items-center gap-2 px-4 py-2 text-sm ${isDark ? 'text-blue-300 hover:text-white' : 'text-blue-700 hover:text-gray-900'} ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-200'} rounded-lg transition-all duration-300 border ${borderColor}`}
              >
                <Icon className="w-4 h-4" />
                {link.label}
              </button>
            );
          })}
        </div>
        
        {/* Buttons */}
        <div className="flex flex-col justify-center gap-3 sm:flex-row">
          <button
            onClick={() => navigate(-1)}
            className={`flex items-center justify-center gap-2 px-6 py-3 ${bgSecondary} ${bgHover} ${textPrimary} rounded-xl transition-all duration-300 border ${borderColor} hover:scale-105 active:scale-95`}
          >
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </button>
          
          <button
            onClick={() => navigate('/')}
            className={`flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r ${buttonGradient} text-white rounded-xl transition-all duration-300 shadow-lg ${shadowColor} hover:scale-105 active:scale-95`}
          >
            <Home className="w-4 h-4" />
            Go Home
          </button>
        </div>

        {/* Footer Text */}
        <p className={`mt-8 text-xs ${textMuted}`}>
          If you believe this is an error, please contact our{' '}
          <button 
            onClick={() => navigate('/contact')}
            className={`${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'} transition-colors`}
          >
            support team
          </button>
        </p>
      </div>

      <style jsx>{`
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.1; }
          50% { opacity: 0.2; }
        }
        .animate-pulse-slow {
          animation: pulse-slow 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2000ms;
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-bounce {
          animation: bounce 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default NotFound;
